import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, FileDown, ShieldCheck } from "lucide-react";
import { api, shortDate } from "../lib/api";
import { useAuth, apiError } from "../lib/auth";
import { PageHeader, NeonButton } from "../components/common";

const Requests = ({ isOwner }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/privacy/requests").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const complete = async (id) => { try { const { data } = await api.post(`/privacy/requests/${id}/complete`); toast.success(`Completed${data.videos_deleted != null ? ` · ${data.videos_deleted} videos deleted` : ""}`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <section className="panel p-5" data-testid="privacy-requests">
      <p className="label-xs">Data-subject requests · public form: <a href="/privacy/request" target="_blank" rel="noreferrer" className="text-brand">/privacy/request</a></p>
      <ul className="mt-3 divide-y divide-divider text-sm">
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex items-center gap-3" data-testid={`privacy-row-${r.id}`}>
            <span className="font-mono text-[10px] uppercase w-14 text-brand">{r.kind}</span><span className="flex-1">{r.email}</span><span className="text-xs text-white/45">{shortDate(r.created_at)}</span>
            {r.completed_at ? <span className="font-mono text-[10px] text-white/45">DONE</span> : isOwner && <button onClick={() => complete(r.id)} className="font-mono text-[10px] text-brand" data-testid={`privacy-complete-${r.id}`}>COMPLETE</button>}
          </li>
        ))}
        {!rows.length && <li className="py-2 text-white/45">No requests.</li>}
      </ul>
    </section>
  );
};

export default function Compliance() {
  const { isOwner } = useAuth();
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/compliance/report").then((r) => setC(r.data)); }, []);
  const pdf = async () => { const r = await api.get("/compliance/report.pdf", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "aegis-compliance-report.pdf"; a.click(); };
  if (!c) return <p className="font-mono text-xs text-white/40">running live checks…</p>;
  return (
    <div className="space-y-6" data-testid="compliance-page">
      <PageHeader eyebrow="// compliance · live controls, computed from real system data" title="Compliance & Legal">
        <NeonButton variant="ghost" onClick={pdf} data-testid="compliance-pdf-btn"><FileDown className="w-3 h-3" /> PDF for Whop legal</NeonButton>
      </PageHeader>
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
        <section className="panel divide-y divide-divider" data-testid="compliance-controls">
          {c.controls.map((x, i) => (
            <div key={x.name} className="p-4 flex gap-3 rise" style={{ animationDelay: `${i * 40}ms` }} data-testid={`control-${i}`}>
              {x.status === "pass" ? <CheckCircle2 className="w-5 h-5 text-brand shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0" />}
              <div><p className="font-bold text-sm">{x.name} <span className={`font-mono text-[10px] ml-2 ${x.status === "pass" ? "text-brand" : "text-amber-300"}`}>{x.status.toUpperCase()}</span></p><p className="text-sm text-white/60 mt-0.5">{x.detail}</p></div>
            </div>
          ))}
        </section>
        <div className="space-y-6">
          <section className="panel p-5" data-testid="compliance-certs">
            <p className="label-xs flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-brand" /> Certifications · honest status</p>
            <ul className="mt-3 space-y-3 text-sm">{c.certifications.map((x) => <li key={x.name}><p className="font-bold">{x.name}</p><p className="text-white/55 text-xs">{x.status}</p></li>)}</ul>
            <p className="text-[11px] text-white/40 mt-4">This report describes technical controls. It is not legal advice or a certification.</p>
          </section>
          <Requests isOwner={isOwner} />
        </div>
      </div>
    </div>
  );
}
